import { useImperativeHandle, useMemo, useRef, useState } from "react";

import {
  PickerChangeHandlerContext,
  TimeValidationError,
} from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { useDidMount, useForceRerender } from "hooks";

import { UseTimePickerParams } from "./TimePicker.types";

export const useTimePicker = <R>({
  ref,
  defaultValue,
  value,
  onChange,
}: UseTimePickerParams<R>) => {
  const rootRef = useRef<HTMLElement | null>(null);
  const timePickerRef = useRef<HTMLElement | null>(null);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [state, setState] = useState<Dayjs | null>(
    value ?? defaultValue ?? null
  );
  const forceRerender = useForceRerender();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: timePickerRef.current,
        value: state,
        setValue: (v: Dayjs | null) => {
          setState(v);
        },
        reset: () => {
          setState(null);
        },
      };
    }) as unknown as () => R,
    [timePickerRef.current, state]
  );

  useDidMount(function init() {
    timePickerRef.current =
      rootRef.current?.querySelector(".MuiFormControl-root") ?? null;
    forceRerender();
  });

  const onChangeHandler = (
    newValue: Dayjs | null,
    context: PickerChangeHandlerContext<TimeValidationError>
  ) => {
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue, context);
  };

  const onCalendarCloseHandler = () => {
    setOpenCalendar(false);
  };

  const toggleCalendar = (event?: React.PointerEvent) => {
    event?.stopPropagation();
    setOpenCalendar((prev) => !prev);
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state),
    [value, state]
  );

  return {
    rootRef,
    timePickerRef,
    valueState,
    openCalendar,
    onChangeHandler,
    onCalendarCloseHandler,
    toggleCalendar,
  };
};
